import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { userDisplayNameAsString } from '../../util/data';
import { propTypes } from '../../util/types';

import Avatar from '../Avatar/Avatar';
import ReviewRating from '../ReviewRating/ReviewRating';

import css from './ReviewCard.module.css';
import Icons from '../Icons/Icons';

const getInitials = name => {
  if (!name || typeof name !== 'string') {
    return '?';
  }
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const initials = parts
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
  return initials || '?';
};

const normalizeRating = rating => {
  const numeric = Number(rating);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  const rounded = Math.round(numeric);
  if (rounded < 1 || rounded > 5) {
    return null;
  }
  return rounded;
};

const normalizeDate = value => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ReviewCard = props => {
  const {
    rootClassName,
    className,
    author,
    authorName,
    authorImage,
    date,
    rating,
    text,
    source,
    sourceLabel,
  } = props;
  const intl = useIntl();
  const [imageFailed, setImageFailed] = useState(false);

  const fallbackName = intl.formatMessage({ id: 'ReviewCard.anonymous' });
  const displayName = authorName || userDisplayNameAsString(author, fallbackName) || fallbackName;
  const dateValue = useMemo(() => normalizeDate(date), [date]);
  const ratingValue = useMemo(() => normalizeRating(rating), [rating]);

  const resolvedSourceLabel =
    sourceLabel ||
    (source === 'google'
      ? intl.formatMessage({ id: 'ReviewCard.sourceGoogle' })
      : source === 'platform'
      ? intl.formatMessage({ id: 'ReviewCard.sourcePlatform' })
      : source || '');

  const hasImage = !!authorImage && !imageFailed;
  const initials = getInitials(displayName);
  const reviewText =
    text && String(text).trim() ? text : intl.formatMessage({ id: 'ReviewCard.noText' });
  const isPlaceholderText = !text || !String(text).trim();

  const classes = classNames(rootClassName || css.root, className);
  const badgeClasses = classNames(css.sourceBadge, {
    [css.sourceGoogle]: source === 'google',
    [css.sourcePlatform]: source === 'platform',
  });

  const ratingLabel = ratingValue
    ? intl.formatMessage({ id: 'ReviewCard.ratingLabel' }, { rating: ratingValue })
    : null;

  return (
    <div className={classes}>
      <div className={css.header}>
        {author ? (
          <Avatar className={css.avatar} user={author} disableProfileLink={false} />
        ) : hasImage ? (
          <div className={css.avatarImage}>
            <img
              src={authorImage}
              alt={displayName}
              onError={() => setImageFailed(true)}
              loading="lazy"
            />
          </div>
        ) : (
          <div className={css.avatarFallback} aria-hidden="true">
            {initials}
          </div>
        )}

        <div className={css.headerMain}>
          <h3 className={css.authorName}>{displayName} </h3>{' '}
          {resolvedSourceLabel ? <span className={badgeClasses}>{resolvedSourceLabel}</span> : null}
        </div>
      </div>
      <div className={css.reviewBody}>
        <p className={classNames(css.text, { [css.placeholderText]: isPlaceholderText })}>
          {reviewText}
        </p>
        {ratingValue ? (
          <span className={css.rating} aria-label={ratingLabel} role="img">
            <ReviewRating rating={ratingValue} reviewStarClassName={css.ratingStar} /> &nbsp; |
            &nbsp; {ratingValue} stars
          </span>
        ) : null}
      </div>
      <div className={css.reviewFooter}>
        {dateValue ? (
          <span className={css.date}>
            <Icons name="calendar" />{' '}
            {intl.formatDate(dateValue, {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ) : null}
        {/* <span className={css.date}>
          <Icons name="location" /> Los Angles
        </span> */}
      </div>
    </div>
  );
};

ReviewCard.defaultProps = {
  rootClassName: null,
  className: null,
  author: null,
  authorName: null,
  authorImage: null,
  date: null,
  rating: null,
  text: null,
  source: null,
  sourceLabel: null,
};

ReviewCard.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
  author: PropTypes.oneOfType([propTypes.user, propTypes.currentUser]),
  authorName: PropTypes.string,
  authorImage: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  text: PropTypes.string,
  source: PropTypes.string,
  sourceLabel: PropTypes.string,
};

export default ReviewCard;
